import { AmountUtils, cn, CurrencyUtils, PaletteUtils } from '@shared/utils';
import { ColorsEnum, Text } from '@shared/widgets';
import type { CardDetailsResponse, CardsResponse } from '@shared/api/rest/cards';
import { CardItem } from '@widgets';
import styles from './accountInfo.module.css';

interface AccountInfoProps {
  className?: string;
  title?: string;
  type1?: string;
  user?: string;
  amount?: string | number;
  type2?: string;
  icon?: string;
  status?: string;
  children?: React.ReactNode;
  card: CardDetailsResponse;
}

export const AccountInfo = (props: AccountInfoProps) => {
  return (
    <div
      className={cn(
        'bg-white dark:bg-soft-black',
        PaletteUtils.isInsigniaPalette && 'rounded-sm',
        PaletteUtils.isVcfcPalette && 'rounded-lg',
        props.className,
      )}
    >
      <div className='flex justify-between p-7 pb-0'>
        <div className='relative z-10 flex-none flex-grow'>
          <Text className='mb-2 break-words' weight='medium' size='2xl'>
            {props.title}
          </Text>
          {PaletteUtils.isVcfcPalette && (
            <div className='hidden max-md:mb-3 max-md:flex max-md:h-11 max-md:items-center max-md:rounded max-md:bg-ice max-md:dark:bg-eerie-black'>
              <div className='ml-1 mr-2 flex h-[29px] w-[29px] items-center justify-center rounded bg-soft-black/80 text-white'>
                {CurrencyUtils.getSymbolByCode(props.card.currency!)}
              </div>
              <Text weight='semibold' size='sm'>
                {props.card.productDisplayName}
              </Text>
              <Text weight='semibold' size='sm' className='ml-auto mr-4'>
                {props.card.status}
              </Text>
            </div>
          )}
          <div className={cn(PaletteUtils.isVcfcPalette && 'flex items-center', PaletteUtils.isInsigniaPalette && 'block')}>
            <div className={cn('flex items-center gap-2', PaletteUtils.isVcfcPalette && 'flex-row-reverse')}>
              <Text weight={PaletteUtils.isVcfcPalette ? undefined : 'medium'} size='xs' color={ColorsEnum.Gray}>
                {props.type1}
              </Text>

              {PaletteUtils.isVcfcPalette && <span className='h-1 w-1 rounded-full bg-ash-blue'></span>}

              <Text
                className={cn(PaletteUtils.isVcfcPalette && 'text-dark-wet-asphalt/60')}
                size={PaletteUtils.isVcfcPalette ? 'sm' : undefined}
                weight={PaletteUtils.isVcfcPalette ? undefined : 'medium'}
              >
                {props.user}
              </Text>
            </div>
          </div>
          {props.amount != null && (
            <Text className={cn('mt-2', PaletteUtils.isInsigniaPalette && 'font-[Prata]')} size='3xl'>
              {props.icon}
              {AmountUtils.transformToAmountWithCurrency(props.amount as number)}
            </Text>
          )}
          {props.type2 && (
            <Text
              className={cn(PaletteUtils.isVcfcPalette && 'mt-1 text-[8px]')}
              weight={PaletteUtils.isVcfcPalette ? undefined : 'medium'}
              size={PaletteUtils.isInsigniaPalette ? 'xs' : undefined}
              color={ColorsEnum.Gray}
              uppercase
            >
              {props.type2}
            </Text>
          )}
        </div>
        {PaletteUtils.isInsigniaPalette && (
          <div
            className={cn(
              'relative flex h-40 w-48 flex-none flex-col items-center justify-end overflow-hidden rounded-tr-sm pb-4 pl-14 pr-1 pt-7 text-center',
              PaletteUtils.isInsigniaPalette && 'bg-soft-black before:border-r-primary dark:bg-slate',
              PaletteUtils.isVcfcPalette && 'bg-primary dark:before:border-r-ice',
              styles['account-info__top-right'],
            )}
          >
            <div
              className={cn(
                'mb-3 before:content-[attr(data-icon)] after:content-[attr(data-icon)]',
                PaletteUtils.isInsigniaPalette && 'before:text-primary after:text-primary',
                PaletteUtils.isVcfcPalette && 'before:text-white after:text-white',
                styles['account-info__icon'],
              )}
              data-icon={props.icon}
            />
            <Text weight='semibold' color={ColorsEnum.White}>
              {props.status}
            </Text>
          </div>
        )}
        {PaletteUtils.isVcfcPalette && (
          <div className='flex items-center justify-center max-md:hidden'>
            <CardItem card={props.card as CardsResponse} />
          </div>
        )}
      </div>
      <div className='px-9 pb-6 pt-3'>{props.children}</div>
    </div>
  );
};
